import * as XLSX from 'xlsx';

/**
 * Reads a CSV/XLSX file into rows of cells, using the `xlsx` dependency the project already
 * has (globalBundleInput.tsx uses the same approach). The ad-ops app uses papaparse; there's
 * no need to add it.
 *
 * Rows come back as raw string cells and are parsed server-side by the same app-ads.txt
 * parser used everywhere else, so an uploaded file behaves identically to a pasted one.
 */
export function readSheetRows(file: File): Promise<string[][]> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheetName = workbook.SheetNames[0];
                if (!firstSheetName) {
                    resolve([]);
                    return;
                }
                const sheet = workbook.Sheets[firstSheetName];
                // header:1 gives arrays-of-cells rather than objects keyed on a header row -
                // the server decides whether row 0 is a header, since it also has to handle
                // the one-cell "full line" shape.
                const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false, raw: false });
                resolve((rows as any[][]).map((r) => (r || []).map((c) => (c == null ? '' : String(c)))));
            } catch (err) {
                reject(err);
            }
        };

        reader.onerror = () => reject(reader.error || new Error('Could not read the file'));
        reader.readAsArrayBuffer(file);
    });
}

/**
 * Reads the onboarding workbook, refusing anything with more than one sheet.
 *
 * readSheetRows takes the first sheet and ignores the rest, which is right for the lines
 * flows - those files are one sheet of lines by construction. It is wrong here: the sheet
 * ad-ops sent has a second tab of scratch domain lookups, and silently dropping a tab someone
 * believed they had uploaded is a data-loss bug rather than a tidy default.
 *
 * Blunt on purpose - more than one sheet is refused whether the extra is populated or not,
 * because "we ignored your empty tab" is a rule people have to remember, and deleting a tab
 * takes ten seconds.
 */
export function readOnboardingSheet(file: File): Promise<string[][]> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: 'array' });
                const names = workbook.SheetNames || [];

                if (names.length > 1) {
                    reject(
                        new Error(
                            `This file has ${names.length} sheets (${names.join(', ')}). ` +
                                `The upload takes a single sheet — please delete the others and try again.`,
                        ),
                    );
                    return;
                }
                if (!names.length) {
                    resolve([]);
                    return;
                }

                const rows = XLSX.utils.sheet_to_json(workbook.Sheets[names[0]], {
                    header: 1,
                    blankrows: false,
                    raw: false,
                });
                resolve((rows as any[][]).map((r) => (r || []).map((c) => (c == null ? '' : String(c)))));
            } catch (err) {
                reject(err);
            }
        };

        reader.onerror = () => reject(reader.error || new Error('Could not read the file'));
        reader.readAsArrayBuffer(file);
    });
}

/** Splits pasted text into rows, for the paste flows that reuse the CSV endpoint. */
export function textToRows(text: string): string[][] {
    return (text || '')
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter((l) => l !== '')
        .map((l) => [l]);
}

/**
 * Reads a plain text file (an app-ads.txt) as-is.
 *
 * Separate from readSheetRows because app-ads.txt is not a spreadsheet - running it through
 * the xlsx reader would mangle it. Used by the manual check path, where the whole point is to
 * hand us the file's literal contents.
 */
export function readTextFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ''));
        reader.onerror = () => reject(reader.error || new Error('Could not read the file'));
        reader.readAsText(file);
    });
}
